import URL from "./apiURL";
import { authenticatedFetch } from "../../utils/authenticatedFetch";

const getUserEmail = () => {
  try {
    const userInfo = JSON.parse(localStorage.getItem("UserInfo"));
    return userInfo?.user_info?.email || null;
  } catch (error) {
    console.debug("Unable to read user email:", error?.message);
    return null;
  }
};

const unwrap = (payload) => {
  if (!payload || typeof payload !== "object") return null;
  if (payload.final_output && typeof payload.final_output === "object") {
    return payload.final_output;
  }
  if (payload.data && typeof payload.data === "object") return payload.data;
  return payload;
};

// This endpoint puts `pagination` beside `final_output`, not inside it.
// Fall back to the nested variants used by other response shapes.
const resolveResponsePagination = (payload, data) => {
  if (payload.pagination && typeof payload.pagination === "object") {
    return payload.pagination;
  }
  if (data.pagination && typeof data.pagination === "object") {
    return data.pagination;
  }
  if (data.paginationData && typeof data.paginationData === "object") {
    return data.paginationData;
  }
  return {};
};

// Shared by fetchBGData and fetchDemandPageSite — both endpoints return the
// same final_output/pagination shape.
const parseDemandPayload = (payload, pagination) => {
  const data = unwrap(payload);
  if (!data || !Array.isArray(data.rows)) {
    console.debug("Demand data: unexpected payload shape:", payload);
    return { level: "BG", expansionOptions: [], weekly_total: {}, rows: [] };
  }
  const responsePagination = resolveResponsePagination(payload, data);
  const rawTotal =
    data.totalRecords ??
    data.total_records ??
    data.total ??
    data.count ??
    responsePagination.totalRecords ??
    responsePagination.total_records ??
    responsePagination.total ??
    responsePagination.count;
  const totalRecords = Number(rawTotal);

  return {
    level: data.level || "BG",
    expansionOptions: Array.isArray(data.expansionOptions) ? data.expansionOptions : [],
    weekly_total: data.weekly_total && typeof data.weekly_total === "object" ? data.weekly_total : {},
    rows: data.rows,
    // The API has used both `pagination` and `paginationData` response shapes.
    // Keep the total separate so callers have a stable value for server pagination.
    totalRecords: Number.isFinite(totalRecords) ? totalRecords : data.rows.length,
    // Some QA responses report `total: pageSize` even when a later page has
    // rows. A full page therefore means there may still be another page.
    hasMore: data.rows.length === Number(pagination.pageSize),
  };
};

// /**
//  * Fetches the BG-level demand grid for the RMPM dashboard.
//  *
//  * @param {Object}  params
//  * @param {Object}  params.filters     - Filter selections, defaults to {}
//  * @param {Object}  params.weekRange   - { from, to } week window
//  * @param {Object}  params.pagination  - { page, pageSize }
//  * @returns {Promise<{ level: string, expansionOptions: string[], weekly_total: Object, rows: Array }>}
//  */
export async function fetchBGData({
  filters = {},
  weekRange = { from: 1, to: 19 },
  pagination = { page: 1, pageSize: 25 },
  parent = null,
  expandDirection = null,
  expandedSiteCode = false,
  sublevelFilters = [],
  signal = undefined,
} = {}) {
  const body = {
    email: getUserEmail(),
    type: "Demand Page",
    filters,
    weekRange,
    pagination,
  };
  if (Array.isArray(sublevelFilters) && sublevelFilters.length) {
    body.sublevel_filters = sublevelFilters;
  }
  if (parent && expandDirection) {
    body.parent = parent;
    body.expandDirection = expandDirection;
    body.expandedSiteCode = !!expandedSiteCode;
  }

  try {
    const payload = await authenticatedFetch(URL.bgData, {
      method: "POST",
      body: JSON.stringify(body),
      signal,
    });

    return parseDemandPayload(payload, pagination);
  } catch (error) {
    if (error?.name === "AbortError") throw error;
    console.error("Error fetching BG data:", error.message);
    throw error;
  }
}

// /**
//  * Fetches the demand grid grouped by site cluster or site code (the
//  * "Select Site Code" filter). Unlike fetchBGData, this endpoint only ever
//  * drills via MATERIALS, and expects `parent`/`expandDirection`/`showSiteCode`
//  * on every request — including the very first one — rather than omitting
//  * them until a drill-down has happened.
//  *
//  * @returns {Promise<{ level: string, expansionOptions: string[], weekly_total: Object, rows: Array }>}
//  */
export async function fetchDemandPageSite({
  filters = {},
  weekRange = { from: 1, to: 19 },
  pagination = { page: 1, pageSize: 25 },
  parent = null,
  showSiteCode = false,
  sublevelFilters = [],
  signal = undefined,
} = {}) {
  const body = {
    email: getUserEmail(),
    type: "Demand Page",
    parent,
    expandDirection: "MATERIALS",
    showSiteCode: !!showSiteCode,
    filters,
    weekRange,
    pagination,
  };
  if (Array.isArray(sublevelFilters) && sublevelFilters.length) {
    body.sublevel_filters = sublevelFilters;
  }

  try {
    const payload = await authenticatedFetch(URL.demandPageSite, {
      method: "POST",
      body: JSON.stringify(body),
      signal,
    });

    return parseDemandPayload(payload, pagination);
  } catch (error) {
    if (error?.name === "AbortError") throw error;
    console.error("Error fetching demand page site data:", error.message);
    throw error;
  }
}